//
//  BloodPressureHistory.tsx
//
//  isana.net,inc. Proprietary and Confidential
//

import { ReactElement, useCallback } from "react";
import { useLocation } from "react-router-dom";

import { Box, Button, Grid, Typography } from "@mui/material";
import BloodPressureSummary from "./component/BloodPressureSummary";
import BloodPressureScatterChart from "./component/BloodPressureScatterChart";
import { FooterWithCorporateLogo } from "./component/FooterWithCorporateLogo";
import BloodPressureChart from "./component/BloodPressureChart";
import { ProgressBar, ProgressStatus } from "./component/ProgressBar";
import { useBloodPressureHistory } from "./hooks/BloodPressureHistory.viewModel";

const BloodPressureHistory = (): ReactElement => {
  const location = useLocation();
  const fitinsightUniqueId = location.state?.fitinsightUniqueId;
  const companyId = location.state?.companyId;
  const measurementDateString = location.state?.measurementDateString;

  const viewmodel = useBloodPressureHistory(
    fitinsightUniqueId,
    companyId,
    measurementDateString
  );

  const { onClickBackButton, onReloadMeasurementData, onReloadTransitionData } =
    viewmodel.handlers;

  const {
    japaneseFormatDateString,
    summaryData,
    transitionChartData,
    scatterChartData,
  } = viewmodel.props;

  // MARK: DOM

  const Header = useCallback(
    () => (
      <Box
        m={2}
        style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
      >
        <ProgressBar status={ProgressStatus.bloodPressureScaned} />
      </Box>
    ),
    []
  );

  /**
   * 血圧データが存在しない場合に表示する
   * @returns DOM
   */
  const NoDataContent = (props: { sx?: object }) => {
    return (
      <Box
        p={15}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography>データがありません</Typography>
      </Box>
    );
  };

  /**
   * 血圧取得失敗時に表示するコンテンツ
   * @param retryHandler 再実行ハンドラ
   * @returns DOM
   */
  const ErrorContent = (props: { retryHandler: () => void }) => {
    return (
      <Box
        p={15}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography pb={1}>データが取得できませんでした</Typography>
        <Button variant="text" onClick={props.retryHandler}>
          もう一度試す
        </Button>
      </Box>
    );
  };

  /**
   * 過去推移表示コンテンツの決定
   * @returns コンテンツのDOM
   */
  const TransitionChart = () => {
    if (transitionChartData.series.length === 0) {
      if (transitionChartData.isError) {
        return <ErrorContent retryHandler={onReloadTransitionData} />;
      } else {
        // エラーではない場合はデータが存在しないと判定する
        return <NoDataContent />;
      }
    }
    return <BloodPressureChart data={transitionChartData.series} />;
  };

  /**
   * 血圧散布図コンテンツの決定
   * @returns コンテンツのDOM
   */
  const ScatterChart = () => {
    if (scatterChartData.series.length === 0) {
      if (scatterChartData.isError) {
        return <ErrorContent retryHandler={onReloadMeasurementData} />;
      } else {
        // エラーではない場合はデータが存在しないと判定する
        return <NoDataContent />;
      }
    }
    // 散布図
    return <BloodPressureScatterChart data={scatterChartData.series} />;
  };

  return (
    <>
      <Header />
      <Box
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        <Grid container width={"95%"}>
          <Grid size={6}>
            <Grid container direction={"column"}>
              <Grid size={6}>
                {measurementDateString && (
                  <BloodPressureSummary
                    measurementDateString={japaneseFormatDateString}
                    measurementData={summaryData}
                  />
                )}
              </Grid>
              <Grid size={6}>
                <Box
                  id="bloodPressureHistoryTransitionChart"
                  display={"flex"}
                  flexDirection={"column"}
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  <Typography variant="h5" fontWeight={"bold"}>
                    直近2週間の血圧・脈拍推移
                  </Typography>
                  <TransitionChart />
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid
            size={6}
            sx={{
              height: 550,
              display: "flex",
              justifyContent: "flex-start",
            }}
          >
            <ScatterChart />
          </Grid>
        </Grid>
        <Button variant="contained" onClick={onClickBackButton}>
          画面を閉じる
        </Button>
        <FooterWithCorporateLogo />
      </Box>
    </>
  );
};

export default BloodPressureHistory;
